import { AbstractCanvasDecorator, CanvasCoordinates, CanvasRenderer, getPointOfStraightLine, normalize, vectorOf } from '../rendering';
import { EmitterEvent } from '../emitter.service';
import { filter } from 'rxjs/operators';
import { isDefined } from '@damofire/data-commons';
import { FreePenRenderer } from './free-pen.renderer';
import { SubSink } from 'subsink';
import { CirclePenRenderer } from './circle-pen.renderer';
import { SquarePenRenderer } from './square-pen.renderer';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CircleAnnotation, FreeAnnotation, PhotoEditorPen, RulerAnnotation, SquareAnnotation, TextAnnotation, UserAnnotation } from './annotation';
import { TextPenRenderer } from './text-pen.renderer';
import { RulerPenRenderer } from './ruler-pen.renderer';

export interface PhotoEditorAnnotationCanvasRenderer<A extends UserAnnotation> extends CanvasRenderer {
  annotation: A;
}

export interface PhotoEditorState {
  pen: PhotoEditorPen;
  color: string;
  annotations: number;
  undoCnt: number;
  redoCnt: number;
}

export class PhotoEditorDecorator extends AbstractCanvasDecorator {
  currentColor: string = null;
  currentPen: PhotoEditorPen = null;
  private editingUserAnnotation: UserAnnotation = null;

  userAnnotations: UserAnnotation[] = [];
  private userAnnotationsRenderers: PhotoEditorAnnotationCanvasRenderer<UserAnnotation>[] = [];
  private readonly mouseSubs: SubSink = new SubSink();

  private undoStack: PhotoEditorAnnotationCanvasRenderer<UserAnnotation>[] = [];
  private redoStack: PhotoEditorAnnotationCanvasRenderer<UserAnnotation>[] = [];

  private _image: HTMLImageElement;
  private readonly state: BehaviorSubject<PhotoEditorState> = new BehaviorSubject<PhotoEditorState>({
    pen: null,
    color: null,
    annotations: 0,
    redoCnt: 0,
    undoCnt: 0,
  });
  $state: Observable<PhotoEditorState> = this.state.asObservable();

  private readonly promptForUserInput: Subject<TextAnnotation> = new Subject<TextAnnotation>();
  $promptForUserInput: Observable<TextAnnotation> = this.promptForUserInput.asObservable();

  constructor(
    protected readonly canvas: HTMLCanvasElement,
    private readonly defaultColor: string,
  ) {
    super(canvas, true);
    this.currentColor = defaultColor;

    this.mouseSubs.add(
      this.mouseDragged
        .pipe(filter(() => isDefined(this.currentPen) && this.currentPen !== 'text'))
        .subscribe((event: EmitterEvent<CanvasCoordinates>) => this.updateDrawing(event.value)),
      this.mouseReleased.subscribe((event: EmitterEvent<CanvasCoordinates>) => this.handleMouseReleaseEvent(event)),
    );
  }

  set image(image: HTMLImageElement) {
    this.userAnnotations.splice(0, this.userAnnotations.length);
    this.userAnnotationsRenderers.splice(0, this.userAnnotationsRenderers.length);

    this._image = image;
    this.renderedBounds = {
      minX: 0,
      minY: 0,
      maxX: image.width,
      maxY: image.height,
      width: image.width,
      height: image.height,
    };
    this.resetZoom(true);
  }

  set annotations(annotations: UserAnnotation[]) {
    this.userAnnotations = annotations || [];
    this.undoStack = [];
    this.redoStack = [];

    if (annotations) {
      this.userAnnotations.forEach((a: UserAnnotation) => {
        let renderer: PhotoEditorAnnotationCanvasRenderer<UserAnnotation>;

        switch (a.type) {
          case 'free':
            renderer = new FreePenRenderer(this.ctx, a as FreeAnnotation);
            break;
          case 'circle':
            renderer = new CirclePenRenderer(this.ctx, a as CircleAnnotation);
            break;
          case 'square':
            renderer = new SquarePenRenderer(this.ctx, a as SquareAnnotation);
            break;
          case 'text':
            renderer = new TextPenRenderer(this.ctx, a as TextAnnotation);
            break;
          case 'ruler':
            renderer = new RulerPenRenderer(this.ctx, a as RulerAnnotation);
            break;
          default:
            console.log(`This should not happen.`);
        }

        this.userAnnotationsRenderers.push(renderer);
      });
    }

    this.renderAll();
    this.state.next({ ...this.state.value, annotations: this.userAnnotations.length, redoCnt: this.redoStack.length, undoCnt: this.undoStack.length });
  }

  updatePen(pen: PhotoEditorPen): void {
    this.editingUserAnnotation = null;
    this.currentPen = pen;
    this.state.next({ ...this.state.value, pen });
  }

  getCursor(eventCoordinates: CanvasCoordinates): string {
    let cursor = super.getCursor(eventCoordinates);

    if (this.currentPen) {
      cursor = 'crosshair';
    }

    return cursor;
  }

  updateColor(color: string): void {
    this.currentColor = color;
    this.state.next({ ...this.state.value, color });
  }

  searchPossibleTargets(eventCoordinates: CanvasCoordinates): any {
    return undefined;
  }

  selectTarget(target: any, coordinates: CanvasCoordinates): void {
    // Not Implemented
  }

  zoomToTarget(): void {
    // Not Implemented
  }

  pan(offsetX: number, offsetY: number): void {
    if (this.currentPen === null) {
      super.pan(offsetX, offsetY);
    }
  }

  redo(): number {
    if (this.redoStack.length) {
      const last = this.redoStack.pop();
      this.undoStack.push(last);
      this.userAnnotations.push(last.annotation);
      this.userAnnotationsRenderers.push(last);

      this.state.next({ ...this.state.value, annotations: this.userAnnotations.length, redoCnt: this.redoStack.length, undoCnt: this.undoStack.length });
      this.renderAll();
    }

    return this.redoStack.length;
  }

  undo(): number {
    if (this.undoStack.length) {
      this.userAnnotationsRenderers.pop();
      const last = this.undoStack.pop();
      this.redoStack.push(last);

      const indexOfLastAnnotation = this.userAnnotations.findIndex((a) => isEqual(a, last));
      this.userAnnotations.splice(indexOfLastAnnotation, 1);

      this.state.next({ ...this.state.value, annotations: this.userAnnotations.length, redoCnt: this.redoStack.length, undoCnt: this.undoStack.length });
      this.renderAll();
    }

    return this.undoStack.length;
  }

  hasChanges(): boolean {
    return this.undoStack.length > 0;
  }

  destroy(): void {
    super.destroy();

    this.userAnnotationsRenderers = null;
    this.userAnnotations = null;
    this.mouseSubs.unsubscribe();
  }

  clearUndoRedoStacks(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.state.next({ ...this.state.value, redoCnt: 0, undoCnt: 0 });
  }

  stopEditing(clearPen = true): void {
    if (this.editingUserAnnotation?.type === 'text' && !(this.editingUserAnnotation as TextAnnotation).text) {
      this.userAnnotations.pop();
      this.userAnnotationsRenderers.pop();
    }

    this.updatePen(clearPen ? null : this.currentPen);
    this.renderAll();
  }

  cancelEditing(clearPen = true): void {
    this.userAnnotations.pop();
    const removed = this.userAnnotationsRenderers.pop();
    const indexOf = this.undoStack.indexOf(removed);

    if (indexOf !== -1) {
      this.undoStack.splice(indexOf, 1);
      this.state.next({ ...this.state.value, annotations: this.userAnnotations.length, redoCnt: this.redoStack.length, undoCnt: this.undoStack.length });
    }

    this.updatePen(clearPen ? null : this.currentPen);
    this.renderAll();
  }

  protected renderComponents(): void {
    if (this._image) {
      this.ctx.save();
      this.ctx.translate(0, this._image.height);
      this.ctx.scale(1, -1);
      this.ctx.drawImage(this._image, 0, 0);
      this.ctx.restore();
    }

    this.userAnnotationsRenderers.forEach((r: CanvasRenderer) => r.draw(this.renderingCtx));
  }

  private updateDrawing(coordinates: CanvasCoordinates, eventCoords?: CanvasCoordinates): void {
    switch (this.currentPen) {
      case 'free':
        this.handleFreeAnnotation(coordinates);
        break;
      case 'circle':
        this.handleCircleAnnotation(coordinates);
        break;
      case 'square':
        this.handleSquareAnnotation(coordinates);
        break;
      case 'text':
        this.handleTextAnnotation(coordinates, eventCoords);
        break;
      case 'ruler':
        this.handleRulerAnnotation(coordinates);
        break;
      default:
        console.log('Pen not supported');
    }

    this.requestRenderAll();
  }

  private handleFreeAnnotation(origin: CanvasCoordinates): void {
    if (this.editingUserAnnotation === null) {
      this.editingUserAnnotation = this.createAnnotation(
        origin,
        (c: CanvasCoordinates) => ({ traceLine: [], origin: c, color: this.currentColor, type: 'free' }) as FreeAnnotation,
        (a: FreeAnnotation) => new FreePenRenderer(this.ctx, a),
      );
    }

    (this.editingUserAnnotation as FreeAnnotation).traceLine.push(origin);
  }

  private handleCircleAnnotation(origin: CanvasCoordinates): void {
    if (this.editingUserAnnotation === null) {
      this.editingUserAnnotation = this.createAnnotation(
        origin,
        (c: CanvasCoordinates) => ({ origin: c, color: this.currentColor, type: 'circle' }) as CircleAnnotation,
        (a: CircleAnnotation) => new CirclePenRenderer(this.ctx, a),
      );
    }

    const circleAnnotation = this.editingUserAnnotation as CircleAnnotation;
    circleAnnotation.end = origin;
    circleAnnotation.radX = Math.abs(circleAnnotation.origin.x - circleAnnotation.end.x) / 2;
    circleAnnotation.radY = Math.abs(circleAnnotation.origin.y - circleAnnotation.end.y) / 2;

    circleAnnotation.x = Math.min(circleAnnotation.origin.x, circleAnnotation.end.x) + circleAnnotation.radX;
    circleAnnotation.y = Math.min(circleAnnotation.origin.y, circleAnnotation.end.y) + circleAnnotation.radY;
  }

  private handleSquareAnnotation(origin: CanvasCoordinates): void {
    if (this.editingUserAnnotation === null) {
      this.editingUserAnnotation = this.createAnnotation(
        origin,
        (c: CanvasCoordinates) => ({ origin: c, color: this.currentColor, type: 'square' }) as SquareAnnotation,
        (a: SquareAnnotation) => new SquarePenRenderer(this.ctx, a),
      );
    }

    const squareAnnotation = this.editingUserAnnotation as SquareAnnotation;
    squareAnnotation.end = origin;
    squareAnnotation.width = Math.abs(squareAnnotation.origin.x - squareAnnotation.end.x);
    squareAnnotation.height = Math.abs(squareAnnotation.origin.y - squareAnnotation.end.y);

    squareAnnotation.x = Math.min(squareAnnotation.origin.x, squareAnnotation.end.x);
    squareAnnotation.y = Math.min(squareAnnotation.origin.y, squareAnnotation.end.y);
  }

  private handleTextAnnotation(origin: CanvasCoordinates, screenOrigin: CanvasCoordinates, direction?: CanvasCoordinates): void {
    if (this.editingUserAnnotation === null) {
      const angle = direction ? Math.atan2(direction.y, direction.x) : 0;
      this.editingUserAnnotation = this.createAnnotation(
        origin,
        (c: CanvasCoordinates) => ({ origin: c, screenOrigin, color: this.currentColor, fontSize: 36, type: 'text', angle }) as TextAnnotation,
        (a: TextAnnotation) => new TextPenRenderer(this.ctx, a),
      );
      this.promptForUserInput.next(this.editingUserAnnotation as TextAnnotation);
    }
  }

  private handleRulerAnnotation(origin: CanvasCoordinates): void {
    if (this.editingUserAnnotation === null) {
      this.editingUserAnnotation = this.createAnnotation(
        origin,
        (c: CanvasCoordinates) => ({ origin: c, color: this.currentColor, fontSize: 36, type: 'ruler' }) as RulerAnnotation,
        (a: RulerAnnotation) => new RulerPenRenderer(this.ctx, a),
      );
    }

    // Après mouse released, on start une annotation 'text', mais on conserve le pen 'ruler'
    if (this.editingUserAnnotation.type === 'ruler') {
      const rulerAnnotation = this.editingUserAnnotation as RulerAnnotation;
      rulerAnnotation.end = origin;
    }
  }

  private startRulerTextAnnotation(coordinates: CanvasCoordinates, screenCoordinates: CanvasCoordinates): void {
    const direction = vectorOf(this.editingUserAnnotation.origin, (this.editingUserAnnotation as RulerAnnotation).end);
    const coordinatesWithOffset = getPointOfStraightLine(coordinates, normalize(direction), 10);

    this.editingUserAnnotation = null;
    this.handleTextAnnotation(coordinatesWithOffset, screenCoordinates, direction);
  }

  private createAnnotation<A extends UserAnnotation>(
    coordinates: CanvasCoordinates,
    annotationFactory: (coordinates: CanvasCoordinates) => A,
    rendererFactory: (a: A) => PhotoEditorAnnotationCanvasRenderer<UserAnnotation>,
  ): A {
    const userAnnotation: A = annotationFactory(coordinates);
    this.userAnnotations.push(userAnnotation);

    const renderer = rendererFactory(userAnnotation);
    this.userAnnotationsRenderers.push(renderer);

    this.undoStack.push(renderer);
    this.redoStack = [];
    this.state.next({ ...this.state.value, annotations: this.userAnnotations.length, redoCnt: this.redoStack.length, undoCnt: this.undoStack.length });
    return userAnnotation;
  }

  private handleMouseReleaseEvent(event: EmitterEvent<CanvasCoordinates>): void {
    if (this.currentPen !== 'text' && this.editingUserAnnotation?.type !== 'text') {
      if (this.currentPen === 'ruler' && this.editingUserAnnotation?.type === 'ruler') {
        this.startRulerTextAnnotation(event.value, event.extras.eventCoords);
      } else {
        this.editingUserAnnotation = null;
      }
    } else if (this.editingUserAnnotation === null && event.event === 'mouseUp') {
      this.updateDrawing(event.value, event.extras.eventCoords);
    }
  }
}
